import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  priceBookIdFormat,
  redoCartIdFormat,
  redoCartItemIdFormat,
} from "@redotech/redo-marketplace-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import * as z from "zod";
import { RedoSuppliesBasePriceSchema } from "./get-redo-supplies-products";

const RedoCartItemSchema_ = z.object({
  id: redoCartItemIdFormat.schema,
  cartId: redoCartIdFormat.schema,
  productId: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  title: z.string(),
  price: z.number(),
  currency: z.string(),
  // Product base price and the price book the line's price came from (null when
  // the base price won) — the client derives any membership-tier display.
  basePrice: RedoSuppliesBasePriceSchema,
  appliedPriceBookId: priceBookIdFormat.schema.nullish(),
  imageUrl: z.string().nullable(),
  options: z.array(z.object({ name: z.string(), value: z.string() })),
  attributes: z.array(z.object({ key: z.string(), value: z.string() })),
  quantity: z.number(),
  maxQuantity: z.number().nullable(),
  // Units in one case for trackable supplies (e.g. 4000 labels/case); null for
  // products without a per-case unit count.
  quantityPerCase: z.number().nullish(),
});
export const RedoCartItemSchema: z.ZodType<RedoCartItem> = RedoCartItemSchema_;
export interface RedoCartItem extends z.infer<typeof RedoCartItemSchema_> {}

/** The discount applied to the cart, if any. Read-only outside checkout. */
const AppliedSuppliesDiscountSchema_ = z.object({
  code: z.string().nullable(),
  storefrontMessage: z.string().nullable(),
  /** Minor units coming off the subtotal; zero while inactive. */
  amount: z.number(),
  active: z.boolean(),
  /** Why it is not applying, when it is not. */
  message: z.string().nullable(),
});
export const AppliedSuppliesDiscountSchema: z.ZodType<AppliedSuppliesDiscount> =
  AppliedSuppliesDiscountSchema_;
export interface AppliedSuppliesDiscount extends z.infer<
  typeof AppliedSuppliesDiscountSchema_
> {}

const RedoCartSchema_ = z.object({
  id: redoCartIdFormat.schema,
  status: z.string(),
  destinationLocationId: locationIdFormat.schema.nullable(),
  items: z.array(RedoCartItemSchema),
  appliedDiscount: AppliedSuppliesDiscountSchema.nullable(),
});
export const RedoCartSchema: z.ZodType<RedoCart> = RedoCartSchema_;
export interface RedoCart extends z.infer<typeof RedoCartSchema_> {}

const RedoCartOutput = z.object({ cart: RedoCartSchema.nullable() });

export const getRedoCart = {
  name: "getRedoCart" as const,
  input: z.object({}),
  output: RedoCartOutput,
};

export const setCartDestinationLocation = {
  name: "setCartDestinationLocation" as const,
  input: z.object({ locationId: locationIdFormat.schema.nullable() }),
  output: RedoCartOutput,
};

export const attachSuppliesReorderToFreeItem = {
  name: "attachSuppliesReorderToFreeItem" as const,
  input: z.object({
    productId: productIdFormat.schema,
    reorderAttributes: z.array(
      z.object({ key: z.string(), value: z.string() }),
    ),
  }),
  output: RedoCartOutput,
};

export const upsertRedoCartItems = {
  name: "upsertRedoCartItems" as const,
  input: z.object({
    productId: productIdFormat.schema,
    productFamilyId: productFamilyIdFormat.schema,
    quantityDelta: z.number(),
    title: z.string(),
    price: z.number(),
    currency: z.string(),
    imageUrl: z.string().nullable(),
    options: z.array(z.object({ name: z.string(), value: z.string() })),
    attributes: z
      .array(z.object({ key: z.string(), value: z.string() }))
      .nullish(),
    // Claim this product as the promotion's free item (qty 1, $0) instead of a
    // normal paid add; the server validates eligibility, threshold, and stock.
    claimFreeItem: z.boolean().nullish(),
  }),
  output: RedoCartOutput,
};
